import { useEffect, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Menu, X } from 'lucide-react';
import { ThemeToggle } from '../../shell/ThemeToggle';
import { useBrand } from '../../brand/BrandProvider';
import { isItcityHost } from '../../brand/host';
import { Wordmark } from './BrandMarks';
import { MarketingFooter } from './MarketingFooter';

/**
 * Shared marketing chrome for the public product pages. Brand-aware (Decision
 * #21): on the platform host it's Possignin's own brand; on a reseller apex the
 * wordmark, nav, and footer wear the RESELLER's brand and drop every
 * platform-only link (Features/Resellers/About/Blog point at Possignin-flavoured
 * content, so they're hidden rather than leaked). Follows the branding seven
 * rules: one accent (`brand`), restraint, Inter, light + dark, mobile-first,
 * tabular numerals.
 */

// Platform nav. On a reseller host we show only the brand-safe surface,
// since Features/About/Blog render platform marketing copy and "Resellers" is a
// platform-only funnel — none belong on a reseller's white-label domain. The
// reseller "Features" entry points at the landing's own #features section and
// "Contact" is the brand's support mailbox (built in MarketingShell, where the
// brand is known).
type NavItem = { label: string; to?: string; hash?: string; href?: string };

const NAV: readonly NavItem[] = [
  { label: 'Features', to: '/features' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Resellers', to: '/become-a-reseller' },
  { label: 'About', to: '/about' },
  { label: 'Blog', to: '/blog' },
] as const;

/**
 * ITCity's own menu, matching the header the Company page (`itcity.ltd/home`)
 * already renders: the company first, its product as a named destination.
 * `/about` and `/features` are Possignin PRODUCT copy, so leading with them on
 * the parent-company domain sold the product under the company's name — the
 * brand leak reported on 2026-07-30. Everything stays a relative route, so a
 * visitor on itcity.ltd never leaves itcity.ltd.
 */
const ITCITY_NAV: readonly NavItem[] = [
  { label: 'Home', to: '/home' },
  { label: 'Possignin', to: '/possignin' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Blog', to: '/blog' },
  { label: 'Partners', to: '/become-a-reseller' },
] as const;

export function MarketingShell({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const brand = useBrand();
  // Reseller "Features" targets /home (not '/'): the bare domain redirects
  // guests to /home and renders the owner dashboard for signed-in users — both
  // would eat the #features hash. /home always renders the landing.
  const nav: readonly NavItem[] = brand.isPlatform
    ? isItcityHost()
      ? ITCITY_NAV
      : NAV
    : [
        { label: 'Home', to: '/' },
        { label: 'Features', to: '/home', hash: 'features' },
        { label: 'Pricing', to: '/pricing' },
        { label: 'Contact', to: '/contact' },
      ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark text-zinc-900 dark:text-zinc-100">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:px-4 focus:py-2 focus:rounded-lg focus:bg-brand focus:text-white focus:text-sm focus:font-medium focus:shadow-lg dark:focus:bg-brand-dm dark:focus:text-zinc-950"
      >
        Skip to content
      </a>

      <header
        className={
          'sticky top-0 z-40 border-b transition-colors duration-200 ' +
          (scrolled
            ? 'border-zinc-200/80 dark:border-zinc-800/80 bg-white/85 dark:bg-zinc-950/85 backdrop-blur-md supports-[backdrop-filter]:bg-white/70 dark:supports-[backdrop-filter]:bg-zinc-950/70'
            : 'border-transparent bg-transparent')
        }
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Wordmark />
          <nav className="hidden md:flex items-center gap-7 text-sm" aria-label="Primary">
            {nav.map((n) =>
              n.href ? (
                <a
                  key={n.label}
                  href={n.href}
                  className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50 transition-colors"
                >
                  {n.label}
                </a>
              ) : (
                <Link
                  key={n.label}
                  to={n.to!}
                  hash={n.hash}
                  className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50 transition-colors"
                >
                  {n.label}
                </Link>
              ),
            )}
          </nav>
          <div className="hidden md:flex items-center gap-2.5">
            <Link
              to="/login"
              className="text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:text-zinc-950 dark:hover:text-zinc-50 px-2 py-1 rounded-md transition-colors"
            >
              Sign in
            </Link>
            <Link
              to="/signup"
              className="group inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-white bg-brand hover:bg-brand-hover shadow-xs transition-colors dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover"
            >
              Start free
              <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
            <ThemeToggle />
          </div>
          {/* Mobile: keep Sign in one-tap visible (not buried in the menu) next to the hamburger. */}
          <div className="md:hidden flex items-center gap-1">
            <Link
              to="/login"
              className="text-sm font-medium text-zinc-700 dark:text-zinc-200 px-3 py-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            >
              Sign in
            </Link>
            <button
              type="button"
              onClick={() => setOpen((s) => !s)}
              className="w-11 h-11 -mr-2 grid place-items-center rounded-lg text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-zinc-200 dark:border-zinc-800 bg-white/95 dark:bg-zinc-950/95 backdrop-blur-md px-4 py-3 space-y-1">
            {nav.map((n) =>
              n.href ? (
                <a
                  key={n.label}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  className="block px-3 py-3 rounded-lg text-[15px] font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  {n.label}
                </a>
              ) : (
                <Link
                  key={n.label}
                  to={n.to!}
                  hash={n.hash}
                  onClick={() => setOpen(false)}
                  className="block px-3 py-3 rounded-lg text-[15px] font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  {n.label}
                </Link>
              ),
            )}
            <div className="flex items-center justify-between px-1 pt-2">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Theme</span>
              <ThemeToggle />
            </div>
            <div className="pt-2 flex items-center gap-2">
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="flex-1 text-center px-3 py-2.5 rounded-lg text-sm font-medium border border-zinc-300 dark:border-zinc-700 text-zinc-800 dark:text-zinc-100"
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                onClick={() => setOpen(false)}
                className="flex-1 text-center px-3 py-2.5 rounded-lg text-sm font-medium text-white bg-brand dark:bg-brand-dm dark:text-zinc-950"
              >
                Start free
              </Link>
            </div>
          </div>
        )}
      </header>

      <main id="main">{children}</main>

      <MarketingFooter />
    </div>
  );
}
